/**
 * BanListSection Component
 * Player ban list with typeahead search and team banning
 */

import ConfirmModal from '@components/ui/ConfirmModal';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { getTeamInfo, getTeamsForLeague, type TeamInfo } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import { ChevronDown } from 'lucide-react';
import type React from 'react';
import { type ChangeEvent, type SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { getPlayerHeadshotUrl, usePlayerIdMapVersion } from '../utils';

// =============================================================================
// Types
// =============================================================================

interface AvailablePlayer {
  id?: string | number;
  name: string;
  teamAbbr?: string;
}

interface BanListSectionProps {
  bannedPlayers: string[];
  onAddPlayer: (playerName: string) => void;
  onAddPlayers: (playerNames: string[]) => void;
  onRemovePlayer: (playerName: string) => void;
  onRemovePlayers: (playerNames: string[]) => void;
  availablePlayers: AvailablePlayer[];
  loadingPlayers: boolean;
  leagueKey: LeagueKey;
}

// Minimum players to show as a team group (otherwise show as individual chips)
const MIN_PLAYERS_FOR_GROUP = 3;

// =============================================================================
// Component
// =============================================================================

export function BanListSection({
  bannedPlayers,
  onAddPlayer,
  onAddPlayers,
  onRemovePlayer,
  onRemovePlayers,
  availablePlayers,
  loadingPlayers,
  leagueKey,
}: BanListSectionProps): React.ReactElement {
  // Subscribe to player ID map changes so headshots in .map() calls update
  usePlayerIdMapVersion();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [isChipsExpanded, setIsChipsExpanded] = useState(
    () => typeof window === 'undefined' || !window.matchMedia('(max-width: 639px)').matches,
  );
  const [dropdownPositions, setDropdownPositions] = useState<Record<string, 'below' | 'above'>>({});
  const chipRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState<string | null>(null);
  const [confirmUnbanTeam, setConfirmUnbanTeam] = useState<string | null>(null);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  // Filter players for typeahead dropdown
  const filteredPlayers = searchQuery.trim()
    ? availablePlayers.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !bannedPlayers.includes(p.name),
      )
    : [];

  // Group banned players by team
  const bannedByTeam = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const playerName of bannedPlayers) {
      const player = availablePlayers.find((p) => p.name === playerName);
      const team = player?.teamAbbr || 'Unknown';
      if (!groups[team]) groups[team] = [];
      groups[team].push(playerName);
    }
    return groups;
  }, [bannedPlayers, availablePlayers]);

  // Count total players per team for "X/Y banned" display
  const teamRosterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const player of availablePlayers) {
      if (player.teamAbbr) {
        counts[player.teamAbbr] = (counts[player.teamAbbr] || 0) + 1;
      }
    }
    return counts;
  }, [availablePlayers]);

  // Separate into team groups (3+ players) and individual players (1-2 players or unknown team)
  const { teamGroups, individualPlayers } = useMemo(() => {
    const teams: Array<{ abbr: string; players: string[] }> = [];
    const individuals: string[] = [];

    for (const [teamAbbr, players] of Object.entries(bannedByTeam)) {
      if (teamAbbr === 'Unknown' || players.length < MIN_PLAYERS_FOR_GROUP) {
        individuals.push(...players);
      } else {
        teams.push({ abbr: teamAbbr, players });
      }
    }

    // Sort teams by number of banned players (descending)
    teams.sort((a, b) => b.players.length - a.players.length);

    return { teamGroups: teams, individualPlayers: individuals };
  }, [bannedByTeam]);

  const handleAddPlayer = (playerName: string): void => {
    onAddPlayer(playerName);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleBanTeam = (teamAbbr: string): void => {
    if (!teamAbbr) return;
    const teamPlayers = availablePlayers.filter((p) => p.teamAbbr === teamAbbr);
    const playerNames = teamPlayers.map((p) => p.name);
    onAddPlayers(playerNames);
  };

  const handleUnbanTeam = (teamAbbr: string): void => {
    const teamPlayers = bannedByTeam[teamAbbr] || [];
    onRemovePlayers(teamPlayers);
  };

  const toggleTeamExpand = (teamAbbr: string): void => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamAbbr)) {
        next.delete(teamAbbr);
      } else {
        next.add(teamAbbr);
      }
      return next;
    });
  };

  // Calculate dropdown position when team is expanded
  useEffect(() => {
    expandedTeams.forEach((teamAbbr) => {
      const chipEl = chipRefs.current[teamAbbr];
      if (chipEl) {
        const rect = chipEl.getBoundingClientRect();
        const dropdownHeight = 200; // max-h-48 = 192px, add some buffer
        const spaceBelow = window.innerHeight - rect.bottom;
        const position = spaceBelow < dropdownHeight ? 'above' : 'below';
        setDropdownPositions((prev) => ({ ...prev, [teamAbbr]: position }));
      }
    });
  }, [expandedTeams]);

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>): void => {
    e.currentTarget.style.display = 'none';
  };

  const leagueTeams = getTeamsForLeague(leagueKey) ?? {};

  return (
    <div className="mb-4" data-testid="ban-list-section">
      <h4 className="text-sm font-medium text-theme-text-secondary mb-2">BANNED PLAYERS</h4>
      <div className="relative">
        {/* Click outside to close dropdown - must be before dropdown in DOM */}
        {showDropdown && (
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
        )}
        <div className="flex flex-col @2xl:flex-row items-stretch @2xl:items-center gap-2">
          {/* Team ban dropdown */}
          <select
            value=""
            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleBanTeam(e.target.value)}
            disabled={loadingPlayers}
            className="w-full @2xl:w-auto px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 bg-theme-surface border border-theme-border text-theme-text focus:ring-theme-primary"
          >
            <option value="">Ban team...</option>
            {Object.entries(leagueTeams)
              // Filter out duplicate entries (LA Clippers vs Los Angeles Clippers)
              .filter(([name]) => !name.startsWith('Los Angeles'))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([teamName, { abbr }]) => (
                <option key={abbr} value={abbr}>
                  {teamName}
                </option>
              ))}
          </select>

          {/* Player search */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={loadingPlayers ? 'Loading players...' : 'Search players to ban...'}
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              disabled={loadingPlayers}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 bg-theme-surface border border-theme-border text-theme-text placeholder-theme-text-tertiary focus:ring-theme-primary"
            />
            {loadingPlayers && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin border-theme-text-tertiary" />
              </div>
            )}
            {/* Typeahead Dropdown */}
            {showDropdown && filteredPlayers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 rounded-lg shadow-xl max-h-64 overflow-y-auto border bg-theme-surface border-theme-border">
                {filteredPlayers.slice(0, 10).map((player) => {
                  const teamData: TeamInfo | undefined = player.teamAbbr
                    ? getTeamInfo(player.teamAbbr, leagueKey)
                    : undefined;
                  const headshotUrl = getPlayerHeadshotUrl(player.name, leagueKey);
                  return (
                    <button
                      type="button"
                      key={player.id || player.name}
                      data-testid="ban-player-suggestion"
                      onClick={() => handleAddPlayer(player.name)}
                      className="w-full flex items-center gap-3 px-3 py-2 transition text-left hover:bg-theme-surface-hover"
                    >
                      {headshotUrl && (
                        <img
                          src={headshotUrl}
                          alt={player.name}
                          className="w-8 h-8 rounded-full object-cover bg-theme-surface-hover"
                          onError={handleImageError}
                        />
                      )}
                      <span className="flex-1 text-sm text-theme-text">{player.name}</span>
                      {(teamData || player.teamAbbr) && (
                        <div className="flex items-center gap-1">
                          {teamData?.logo && (
                            <img
                              src={teamData.logo}
                              alt={player.teamAbbr}
                              className="w-5 h-5 object-contain"
                            />
                          )}
                          <span className="text-xs text-theme-text-secondary">
                            {player.teamAbbr}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Banned Players Display */}
      {bannedPlayers.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div
              onClick={() => setIsChipsExpanded((prev) => !prev)}
              className="flex items-center gap-1 cursor-pointer flex-1 min-w-0"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsChipsExpanded((prev) => !prev);
                }
              }}
            >
              <ChevronDown
                className={cn(
                  'w-4 h-4 shrink-0 transition-transform',
                  !isChipsExpanded && '-rotate-90',
                )}
              />
              <span className="text-xs text-theme-text-tertiary">
                {bannedPlayers.length} banned
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowClearAllConfirm(true)}
              className="text-xs text-theme-danger hover:text-theme-danger/80"
            >
              Clear all
            </button>
          </div>

          {isChipsExpanded && (
            <>
              {/* Click outside to close any expanded team dropdown */}
              {expandedTeams.size > 0 && (
                <div className="fixed inset-0 z-40" onClick={() => setExpandedTeams(new Set())} />
              )}

              {/* All banned items in a single flex wrap container */}
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2">
                {/* Team Groups - displayed as chips */}
                {teamGroups.map(({ abbr, players }) => {
                  const teamInfo = getTeamInfo(abbr, leagueKey);
                  const rosterCount = teamRosterCounts[abbr] || players.length;
                  const isExpanded = expandedTeams.has(abbr);
                  const dropdownPosition = dropdownPositions[abbr] || 'below';

                  return (
                    <div
                      key={abbr}
                      className="relative"
                      ref={(el) => {
                        chipRefs.current[abbr] = el;
                      }}
                    >
                      {/* Team Chip */}
                      <div className="flex items-center gap-1 rounded-full pl-1 pr-2 py-1 border bg-theme-danger/15 border-theme-danger/30">
                        {teamInfo?.logo && (
                          <img
                            src={teamInfo.logo}
                            alt={abbr}
                            loading="lazy"
                            decoding="async"
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => toggleTeamExpand(abbr)}
                          className="text-sm text-theme-danger hover:text-theme-danger/80"
                        >
                          {abbr} ({players.length}/{rosterCount})
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmUnbanTeam(abbr)}
                          className="font-bold text-sm ml-1 text-theme-danger hover:text-theme-danger/70"
                        >
                          &times;
                        </button>
                      </div>

                      {/* Expanded Player Dropdown */}
                      {isExpanded && (
                        <div
                          className={cn(
                            'absolute z-50 left-0 rounded-lg shadow-xl p-2 min-w-48 max-w-64 max-h-48 overflow-y-auto border bg-theme-surface border-theme-border',
                            dropdownPosition === 'above' ? 'bottom-full mb-1' : 'top-full mt-1',
                          )}
                        >
                          <div className="flex flex-wrap gap-1">
                            {players.map((playerName) => (
                              <div
                                key={playerName}
                                className="flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-xs bg-theme-danger/15"
                              >
                                <span className="text-theme-danger">{playerName}</span>
                                <button
                                  type="button"
                                  onClick={() => setConfirmRemovePlayer(playerName)}
                                  className="font-bold px-1 text-theme-danger hover:text-theme-danger/70"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Individual Players */}
                {individualPlayers.map((playerName) => {
                  const player = availablePlayers.find((p) => p.name === playerName);
                  const teamInfo = player?.teamAbbr
                    ? getTeamInfo(player.teamAbbr, leagueKey)
                    : undefined;
                  const headshotUrl = getPlayerHeadshotUrl(playerName, leagueKey);
                  return (
                    <div
                      key={playerName}
                      className="flex items-center gap-1 rounded-full pl-1 pr-2 py-1 border bg-theme-danger/15 border-theme-danger/30"
                    >
                      {teamInfo?.logo && (
                        <img
                          src={teamInfo.logo}
                          alt={player?.teamAbbr}
                          className="w-5 h-5 object-contain"
                        />
                      )}
                      {headshotUrl && (
                        <img
                          src={headshotUrl}
                          alt={playerName}
                          className="w-6 h-6 rounded-full object-cover"
                          onError={handleImageError}
                        />
                      )}
                      <span className="text-sm text-theme-danger">{playerName}</span>
                      <button
                        type="button"
                        onClick={() => setConfirmRemovePlayer(playerName)}
                        className="font-bold text-sm ml-1 text-theme-danger hover:text-theme-danger/70"
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={!!confirmRemovePlayer}
        title="Remove from Ban List"
        message={`Are you sure you want to unban ${confirmRemovePlayer ?? ''}?`}
        confirmLabel="Unban"
        isDestructive
        onConfirm={() => {
          if (confirmRemovePlayer) onRemovePlayer(confirmRemovePlayer);
          setConfirmRemovePlayer(null);
        }}
        onCancel={() => setConfirmRemovePlayer(null)}
      />

      <ConfirmModal
        isOpen={!!confirmUnbanTeam}
        title="Unban Team"
        message={`Remove all ${confirmUnbanTeam ? (bannedByTeam[confirmUnbanTeam]?.length ?? 0) : 0} ${confirmUnbanTeam ?? ''} players from the ban list?`}
        confirmLabel="Unban All"
        isDestructive
        onConfirm={() => {
          if (confirmUnbanTeam) handleUnbanTeam(confirmUnbanTeam);
          setConfirmUnbanTeam(null);
        }}
        onCancel={() => setConfirmUnbanTeam(null)}
      />

      <ConfirmModal
        isOpen={showClearAllConfirm}
        title="Clear Ban List"
        message={`Remove all ${bannedPlayers.length} players from the ban list?`}
        confirmLabel="Clear All"
        isDestructive
        onConfirm={() => {
          onRemovePlayers(bannedPlayers);
          setShowClearAllConfirm(false);
        }}
        onCancel={() => setShowClearAllConfirm(false)}
      />
    </div>
  );
}
